import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { makeRequest } from "../../../utils/axios.js"; 
import { ethers } from "ethers";
import { useState } from "react";

export const usePostInteractions = (post, currentUser) => {
    const queryClient = useQueryClient();

    const [isTipping, setIsTipping] = useState(false);

    const { isLoading, data: likes } = useQuery({
        queryKey: ["likes", post.id],
        queryFn: () => makeRequest.get("/likes?postId=" + post.id).then((res) => res.data),
    });

    const { data: comments } = useQuery({
        queryKey: ["comments", post.id],
        queryFn: () => makeRequest.get("/comments?postId=" + post.id).then((res) => res.data),
    });

    const likeMutation = useMutation({
        mutationFn: (isLiked) => {
            if (isLiked) return makeRequest.delete("/likes?postId=" + post.id);
            return makeRequest.post("/likes", { postId: post.id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["likes"] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (postId) => makeRequest.delete("/posts/" + postId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["posts"] });
        },
    });

    const safeLikes = Array.isArray(likes) ? likes : [];
    const isLiked = safeLikes.some(id => String(id) === String(currentUser.id));

    const handleLike = () => {
        likeMutation.mutate(isLiked);
    };

    const handleDelete = () => {
        deleteMutation.mutate(post.id);
    };

    const handleTip = async () => {
        if (post.userId === currentUser.id) {
            return alert("You cannot tip your own post!");
        }
        
        if (!post.walletAddress) {
            return alert("This author hasn't linked a Web3 wallet yet!");
        }

        if (!window.ethereum) {
            return alert("Please install MetaMask to use this feature!");
        }

        try {
            setIsTipping(true);
            
            const provider = new ethers.BrowserProvider(window.ethereum);
            await provider.send("eth_requestAccounts", []);
            const signer = await provider.getSigner();

            const tipAmount = "0.001"; 
            const tx = await signer.sendTransaction({
                to: post.walletAddress,
                value: ethers.parseEther(tipAmount)
            });

            alert(`Transaction sent! Waiting for confirmation... (Hash: ${tx.hash.substring(0, 10)}...)`);
            await tx.wait(); 
            
            alert("Tip successful! Thank you for supporting the author.");
        } catch (error) {
            console.error("Tipping error:", error);
            if (error.code === 4001) {
                alert("You rejected the transaction in MetaMask.");
            } else {
                alert("Transaction failed. Please check your balance or network.");
            }
        } finally {
            setIsTipping(false);
        }
    };

    const getBadgeStyle = (type) => {
        switch (type) {
            case "Question":
                return { backgroundColor: "#ffe0b2", color: "#e65100" };
            case "Proof attempt":
                return { backgroundColor: "#e3f2fd", color: "#0d47a1" };
            case "Counterexample":
                return { backgroundColor: "#f3e5f5", color: "#4a148c" };
            case "Research idea":
                return { backgroundColor: "#e8f5e9", color: "#1b5e20" };
            case "Discussion":
            default:
                return { backgroundColor: "#f5f5f5", color: "#424242" };
        }
    };

    return {
        likesCount: safeLikes.length,
        commentCount: comments?.length,
        isLiked,
        isLoadingLikes: isLoading,
        isTipping,
        handleLike,
        handleDelete,
        handleTip,
        getBadgeStyle,
    };
};